import type { AxiosInstance } from "axios";
import { Loader2, Power, PowerOff, TriangleAlert, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { apiErrorMessage } from "../lib/api.js";
import { cn } from "../lib/cn.js";
import {
  blockedMessage,
  canSubmitDisable,
  changeActor,
  changeLabel,
  disableTracking,
  enableTracking,
  fetchTracking,
  formatChangeTime,
  REASON_MAX_LENGTH,
  REASON_MIN_LENGTH,
  TAKES_EFFECT_MINUTES,
  TRACKING_CHANGED_EVENT,
  trackingHeadline,
  type TrackingStatus,
  type TrackingTone,
} from "../lib/tracking.js";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card.js";
import { Input } from "./ui/input.js";
import { Label } from "./ui/label.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table.js";
import { Textarea } from "./ui/textarea.js";

/**
 * The Ecomally Plugin tab: is this store's tracking on, the switch, and every change to it
 * (ticket "Plugin Control", part 1).
 *
 * SWITCHING OFF IS DELIBERATELY SLOW — a dialog asking for a reason, the member's password and an
 * explicit "I understand" — because what it leaves behind is a gap in the store's data that nothing
 * can fill later. Switching back on is one click: the cost of an accidental "on" is nothing.
 */
const BADGE_TONE: Record<TrackingTone, string> = {
  ok: "border-emerald-400 text-emerald-700 dark:text-emerald-400",
  off: "border-amber-400 text-amber-700 dark:text-amber-400",
  blocked: "border-destructive/50 text-destructive",
};

export function TrackingPluginPanel({
  client,
  storeName,
}: {
  readonly client: AxiosInstance;
  readonly storeName: string;
}): React.JSX.Element {
  const [status, setStatus] = useState<TrackingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [enabling, setEnabling] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await fetchTracking(client));
      setError(null);
    } catch (caught) {
      // Cleared, not kept: a stale "On" is exactly the false reassurance this tab exists to avoid.
      setStatus(null);
      setError(apiErrorMessage(caught, "Could not load the tracking status."));
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  if (status === null) {
    return error === null ? (
      <p className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" />
        Loading tracking status…
      </p>
    ) : (
      <Card className="border-destructive/40">
        <CardContent className="text-destructive text-sm">{error}</CardContent>
      </Card>
    );
  }

  const headline = trackingHeadline(status);
  const blocked = blockedMessage(status.blockedBy);
  const latest = status.history[0];
  const canSwitchOff = status.blockedBy === null && status.enabled;
  // Ecomally's pause and a paused org arrive as a block, so an unblocked "off" is the merchant's own.
  const canSwitchOn = status.blockedBy === null && !status.enabled;

  const switchOn = async (): Promise<void> => {
    setEnabling(true);
    setError(null);
    try {
      setStatus(await enableTracking(client));
      window.dispatchEvent(new Event(TRACKING_CHANGED_EVENT));
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not switch tracking back on."));
    } finally {
      setEnabling(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex-row flex-wrap items-start justify-between gap-4 space-y-0">
          {/* Shrinks before the button wraps: the switch stays on the right until the screen is narrow. */}
          <div className="flex min-w-0 flex-1 basis-72 flex-col gap-1.5">
            <CardTitle className="flex items-center gap-2">
              Ecomally tracking
              <Badge variant="outline" className={BADGE_TONE[headline.tone]} data-testid="tracking-badge">
                {headline.label}
              </Badge>
            </CardTitle>
            <CardDescription>{headline.detail}</CardDescription>
          </div>
          {canSwitchOff && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setDialogOpen(true)}
            >
              <PowerOff />
              Switch off…
            </Button>
          )}
          {canSwitchOn && (
            <Button size="sm" onClick={() => void switchOn()} disabled={enabling}>
              {enabling ? <Loader2 className="animate-spin" /> : <Power />}
              Switch back on
            </Button>
          )}
        </CardHeader>

        {(latest !== undefined || blocked !== null || error !== null) && (
          <CardContent className="flex flex-col gap-3 text-sm">
            {latest !== undefined && (
              <div className="text-muted-foreground">
                <span className="text-foreground font-medium">{changeLabel(latest)}</span> by {changeActor(latest)} ·{" "}
                {formatChangeTime(latest.at)}
                {latest.reason !== null && (
                  <p className="bg-muted/60 text-foreground mt-2 rounded-md border px-3 py-2">“{latest.reason}”</p>
                )}
              </div>
            )}
            {blocked !== null && <p className="text-muted-foreground">{blocked}</p>}
            {error !== null && (
              <p className="text-destructive" role="alert">
                {error}
              </p>
            )}
          </CardContent>
        )}
      </Card>

      <Card className="gap-4">
        <CardHeader>
          <CardTitle className="text-base">History</CardTitle>
          <CardDescription>Every time tracking for this store was switched off or on, with who did it and why.</CardDescription>
        </CardHeader>
        <CardContent>
          {status.history.length === 0 ? (
            <p className="text-muted-foreground text-sm">No changes yet.</p>
          ) : (
            // Its own scroll, so a long history never pushes the page down.
            <div className="max-h-80 overflow-auto rounded-md border">
              {/* A minimum width, so a narrow screen scrolls the table rather than crushing the reason. */}
              <Table className="min-w-[760px]">
                <TableHeader className="bg-card sticky top-0">
                  <TableRow>
                    <TableHead className="w-48">When</TableHead>
                    <TableHead className="w-40">Change</TableHead>
                    <TableHead className="w-48">By</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {status.history.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-muted-foreground whitespace-nowrap">{formatChangeTime(entry.at)}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-2">
                          <span
                            aria-hidden
                            className={cn("size-1.5 rounded-full", entry.status === "paused" ? "bg-amber-500" : "bg-emerald-500")}
                          />
                          {changeLabel(entry)}
                        </span>
                      </TableCell>
                      <TableCell title={entry.actorEmail ?? undefined}>{changeActor(entry)}</TableCell>
                      <TableCell className="text-muted-foreground whitespace-normal">{entry.reason ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {dialogOpen && (
        <DisableTrackingDialog
          storeName={storeName}
          onClose={() => setDialogOpen(false)}
          onSubmit={async (input) => {
            setStatus(await disableTracking(client, input));
            setDialogOpen(false);
            window.dispatchEvent(new Event(TRACKING_CHANGED_EVENT));
          }}
        />
      )}
    </div>
  );
}

/** The switch-off confirmation: what it costs, a reason, the member's password, and "I understand". */
export function DisableTrackingDialog({
  storeName,
  onClose,
  onSubmit,
}: {
  readonly storeName: string;
  readonly onClose: () => void;
  readonly onSubmit: (input: { readonly password: string; readonly reason: string }) => Promise<void>;
}): React.JSX.Element {
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  // Escape closes (not mid-request), focus starts on the reason, and the page behind stops scrolling.
  useEffect(() => {
    reasonRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose, busy]);

  const ready = canSubmitDisable({ reason, password, understood });
  const length = reason.trim().length;

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ password, reason: reason.trim() });
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not switch tracking off."));
      // A wrong password is the usual cause; make them type it again rather than resend it.
      setPassword("");
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="disable-tracking-title"
        onSubmit={(event) => void submit(event)}
        className="bg-background flex w-full max-w-lg flex-col gap-5 rounded-xl border p-6 shadow-lg"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 id="disable-tracking-title" className="text-lg font-semibold">
              Switch off tracking for {storeName}?
            </h2>
            <p className="text-muted-foreground text-sm">This stops all data from this store, not only website visits.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="text-muted-foreground hover:bg-muted hover:text-foreground -mt-1 -mr-2 cursor-pointer rounded-md p-1.5 transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          <p className="flex items-center gap-2 font-medium">
            <TriangleAlert className="size-4 shrink-0" />
            While tracking is off
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-9">
            <li>No visits, orders or refunds from this store are recorded.</li>
            <li>That gap is permanent — switching back on later does not recover it.</li>
            <li>Reports and attribution for those days will show less than really happened.</li>
            <li>It can take up to {TAKES_EFFECT_MINUTES} minutes to stop.</li>
          </ul>
        </div>

        <div className="grid gap-1.5">
          <div className="flex items-baseline justify-between">
            <Label htmlFor="disable-reason">Reason</Label>
            <span className={cn("text-xs tabular-nums", length > REASON_MAX_LENGTH ? "text-destructive" : "text-muted-foreground")}>
              {length}/{REASON_MAX_LENGTH}
            </span>
          </div>
          <Textarea
            id="disable-reason"
            ref={reasonRef}
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="For example: moving to a new theme this week"
            disabled={busy}
          />
          <p className="text-muted-foreground text-xs">
            At least {REASON_MIN_LENGTH} characters. Saved in the history with your name.
          </p>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="disable-password">Your password</Label>
          <Input
            id="disable-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={busy}
          />
        </div>

        <label className="flex cursor-pointer items-start gap-2.5 text-sm">
          <input
            type="checkbox"
            className="accent-primary mt-0.5 size-4"
            checked={understood}
            onChange={(event) => setUnderstood(event.target.checked)}
            disabled={busy}
          />
          I understand this leaves a permanent gap in this store's data.
        </label>

        {error !== null && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="destructive" disabled={!ready || busy}>
            {busy && <Loader2 className="animate-spin" />}
            Switch off tracking
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
